// Design tokens for the school leadership dashboard (iOS-style glass, light).
export const C = {
  ink: '#0B0D17',
  ink2: '#3C4257',
  mu: '#6E7489',
  dim: '#A3A8B8',
  line: 'rgba(15,20,40,0.08)',
  line2: 'rgba(15,20,40,0.05)',
  glass: 'rgba(255,255,255,0.66)',
  glassStrong: 'rgba(255,255,255,0.82)',
  canvas: '#EEF0F7',

  // iOS system colours
  blue: '#0A7CFF',
  blueL: 'rgba(10,124,255,0.12)',
  indigo: '#5E5CE6',
  indigoL: 'rgba(94,92,230,0.12)',
  green: '#28B463',
  greenL: 'rgba(40,180,99,0.13)',
  greenInk: '#14804A',
  orange: '#FF9500',
  orangeL: 'rgba(255,149,0,0.14)',
  orangeInk: '#B05F00',
  red: '#FF3B30',
  redL: 'rgba(255,59,48,0.12)',
  redInk: '#C4271D',
  teal: '#14B8C6',
  tealL: 'rgba(20,184,198,0.13)',
  pink: '#FF2D78',
  purple: '#AF52DE',

  // Hero / dark surfaces
  night: '#0E1024',
  night2: '#1A1D3A',
  nightMu: '#9A9FC2',
};

export const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Plus Jakarta Sans", "Inter", system-ui, sans-serif';
export const FONT_DISPLAY = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Plus Jakarta Sans", "Inter", system-ui, sans-serif';

export const EASE = [0.16, 1, 0.3, 1] as const;
export const SPRING = { type: 'spring' as const, stiffness: 380, damping: 32 };

/** Performance tiers, best → worst. Thresholds match the teacher dashboard's bands. */
export const TIERS = [
  { key: 'excelling', name: '80%+', label: 'Top performers', min: 80, color: C.green, soft: C.greenL, ink: C.greenInk },
  { key: 'proficient', name: '60–79%', label: 'Proficient', min: 60, color: C.blue, soft: C.blueL, ink: '#0A5FC2' },
  { key: 'developing', name: '35–59%', label: 'Developing', min: 35, color: C.orange, soft: C.orangeL, ink: C.orangeInk },
  { key: 'support', name: '<35%', label: 'Needs support', min: 0, color: C.red, soft: C.redL, ink: C.redInk },
] as const;

export type TierIndex = 0 | 1 | 2 | 3;

export const tierOf = (pct: number): TierIndex => (pct >= 80 ? 0 : pct >= 60 ? 1 : pct >= 35 ? 2 : 3);

/** Colour for an average score. */
export const scoreTone = (pct: number | null | undefined) => {
  if (pct == null) return { fg: C.mu, bg: 'rgba(15,20,40,0.05)' };
  const t = TIERS[tierOf(pct)];
  return { fg: t.ink, bg: t.soft };
};

/** Signed number with a real minus sign, e.g. +4.2 / −1.0. */
export const signed = (v: number, decimals = 0) =>
  `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v).toFixed(decimals)}`;

export const pct = (v: number | null | undefined, decimals = 0) =>
  v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(decimals)}%`;
